/**
 * Frontend types for the strategic budget bucket feature.
 *
 * The backend computes `usedAmount` and `remainingAmount` from the bucket's
 * audit-log transactions on every read, so these are always fresh — no
 * caching layer to worry about on the FE.
 */

export enum BucketTransactionType {
  WITHDRAW = "WITHDRAW",
  RELEASE = "RELEASE",
  UNFORESEEN_WITHDRAW = "UNFORESEEN_WITHDRAW",
  ADJUSTMENT = "ADJUSTMENT",
}

/**
 * Waar het bedrag van een strategisch budget vandaan komt.
 *
 * `acquisitionRenovationBudget` laat het bedrag het gebouw volgen (Financieel →
 * "+ Renovatiebudget (incl. btw) bij aankoop"). Het acquisitiebudget stond met de
 * hand op € 152.000 terwijl het gebouw € 300.000 aangaf; datzelfde cijfer op twee
 * plaatsen bijhouden loopt gegarandeerd uit elkaar.
 */
export enum BucketAmountSource {
  MANUAL = "manual",
  ACQUISITION_RENOVATION_BUDGET = "acquisitionRenovationBudget",
}

export interface BudgetBucketView {
  id: number;
  buildingId: string;
  name: string;
  year: number | null;
  totalAmount: number;
  /** Bij `acquisitionRenovationBudget` is het bedrag alleen-lezen. */
  amountSource?: BucketAmountSource;
  /** Sum of WITHDRAW + UNFORESEEN_WITHDRAW + ADJUSTMENT − RELEASE. */
  usedAmount: number;
  /** May be negative when the bucket is overspent. */
  remainingAmount: number;
  isApproved: boolean;
  approvedAt: string | null;
  approvedByUserId: string | null;
  notes: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  investmentItemCount: number;
  transactionCount: number;
  /**
   * Sum across non-terminal investment lines on this bucket of
   * `estimatedCostExclVat − allocatedBudget`. The slack already inside
   * lines that hasn't been committed to project parts yet — surfaced on
   * the card as "nog ongebruikt in projectonderdelen".
   */
  unallocatedInLinesAmount: number;
  /**
   * Sum across non-terminal lines on this bucket of
   * `min(estimatedCostExclVat, allocatedBudget)` — the part of project-part
   * commitments that fits inside each line's original raming. Surfaces on
   * the card as "vastgelegd binnen raming".
   */
  committedWithinEstimateAmount: number;
  /**
   * Sum across non-terminal lines on this bucket of
   * `max(0, allocatedBudget − estimatedCostExclVat)` — the part of
   * commitments that exceeded the line's original raming. This is *NOT* a
   * strategic-budget deficit on its own: while `remainingAmount > 0` it
   * can be absorbed by re-detailing the line. Surfaced on the card as a
   * soft amber "raming overschreden" warning, separate from the bucket
   * overspend (red) state.
   */
  estimateOverrunAmount: number;
  /**
   * Worst-offending investment lines on this bucket where allocations
   * exceed the original line raming. Sorted desc by overrun, max 3 items.
   * Surfaced on the card so a PM can immediately triage which line is
   * driving the overschrijding (often a single typo can dominate totals).
   */
  topOverrunLines: Array<{
    id: number;
    title: string;
    estimatedCostExclVat: number;
    allocatedBudget: number;
    overrunAmount: number;
  }>;
  /**
   * Set on the synthetic "Budget overflow" entry returned by the backend.
   * The card renders read-only (no edit / delete / approve / drawer) and
   * uses the warning palette.
   */
  isOverflow?: boolean;
}

export interface BucketTransactionView {
  id: number;
  type: BucketTransactionType;
  amount: number;
  reason: string | null;
  createdByUserId: string | null;
  createdAt: string;
  /** ID of the investment line that triggered the movement, if any. */
  investmentItem: { id: number; title: string } | null;
  /** Convenience flat field — backend includes this alongside the relation. */
  investmentItemTitle: string | null;
  projectPart: { id: number; title: string } | null;
  projectPartTitle: string | null;
}

export interface CreateBudgetBucketDto {
  buildingId: string;
  name: string;
  year?: number | null;
  totalAmount: number;
  /** Weglaten = handmatig. Zie `BucketAmountSource`. */
  amountSource?: BucketAmountSource;
  notes?: string | null;
}

export interface UpdateBudgetBucketDto {
  name?: string;
  year?: number | null;
  totalAmount?: number;
  amountSource?: BucketAmountSource;
  notes?: string | null;
}
